import { fetchOverlappingTAEDValidation } from "../api/taed";
import {
  FOUR_HOURS_IN_MINUTES,
  FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT,
  IAM_CONTRACT,
  MAX_IAM_SHIFT_LENGTH_MINUTES,
  MAX_NON_CONTRACT_SHIFT_LENGTH_MINUTES,
  MAX_TWU_SHIFT_LENGTH_MINUTES,
  NON_CONTRACT,
  TWENTY_FOUR_HOURS_IN_MINUTES,
  TWU_CONTRACT,
  WEEKDAYS_SHORT,
} from "./constants";
import { TWO_MONTHS_IN_PAST, ONE_YEAR_FROM, THREE_YEARS_IN_PAST } from "./dateHelper";
import {
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  END_DATE_BEFORE_START_DATE_ERROR,
  END_DATE_CANNOT_BE_MORE_THAN_TWO_MONTHS_IN_PAST,
  END_DATE_IS_REQUIRED,
  INVALID_EMPLOYEE_ID,
  MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MINIMUM_SHIFT_LENGTH_WARNING__4_HOURS,
  SHIFT_OVERLAPS_WITH_PREVIOUS_SHIFT_WARNING_MESSAGE,
  START_DATE_AFTER_END_DATE_ERROR,
  START_DATE_IS_REQUIRED,
  TEMP_ASSIGNMENT_SELF_ACTION_CREATE_WARNING_MESSAGE,
} from "./messageConstants";

// ***** Employee Info Validation Start ***** //

export function validateEmployeeDataForTemporaryAssignment(selectedEid, userSessionEid) {
  if (selectedEid === null) {
    return "Error loading Employee data";
  }

  if (!selectedEid.isActive) {
    return INVALID_EMPLOYEE_ID;
  }
  if (selectedEid.eid === userSessionEid) {
    return TEMP_ASSIGNMENT_SELF_ACTION_CREATE_WARNING_MESSAGE;
  }
  return "";
}

export const validateTAEDStartDate = (startDate, endDate) => {
  if (startDate === null) {
    return START_DATE_IS_REQUIRED;
  }

  if (endDate === null) {
    return "";
  }

  if (startDate.toMillis() > endDate.toMillis()) {
    return START_DATE_AFTER_END_DATE_ERROR;
  }

  return "";
};

export const validateTAEDEndDate = (startDate, endDate, station) => {
  if (endDate === null) {
    return END_DATE_IS_REQUIRED;
  }

  if (startDate === null) {
    return "";
  }

  if (endDate.toMillis() < TWO_MONTHS_IN_PAST(station).toMillis()) {
    return END_DATE_CANNOT_BE_MORE_THAN_TWO_MONTHS_IN_PAST;
  }

  if (endDate.toMillis() < startDate.toMillis()) {
    return END_DATE_BEFORE_START_DATE_ERROR;
  }

  return "";
};

export const validateTAEDComments = (inputComments, isEditTempAssignment) => {
  if (isEditTempAssignment) {
    if (inputComments === "") {
      return COMMENTS_REQUIRED;
    }
  }
  if (inputComments.length > FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT) {
    return CHAR_LIMIT_MESSAGE(FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT);
  }
  return "";
};

export const validateOverlappingTAED = async (startDate, endDate, employeeID, referenceNumber) => {
  const tempAssignment = {
    startDate: startDate,
    endDate: endDate,
    eid: employeeID,
    referenceNumber: referenceNumber,
  };
  const overlapError = await fetchOverlappingTAEDValidation(tempAssignment);
  return overlapError;
};

export function validateTAEDStartDateFilter(input, endDateFilter, station) {
  if (input === null) {
    return START_DATE_IS_REQUIRED;
  }

  if (endDateFilter.toMillis() < input.toMillis()) {
    return "End Date cannot be prior to Start Date";
  }
  if (input.toMillis() < THREE_YEARS_IN_PAST(station).toMillis()) {
    return "Start Date cannot be prior to 3 years in the past";
  }
  if (endDateFilter.toMillis() > ONE_YEAR_FROM(station, input).toMillis()) {
    return "Start Date cannot be over 1 year before End Date";
  }

  return "";
}

export const validateTAEDEndDateFilter = (input, startDateFilter, station) => {
  if (input === null) {
    return END_DATE_IS_REQUIRED;
  }

  if (input.toMillis() < startDateFilter.toMillis()) {
    return "End Date cannot be prior to Start Date";
  }
  if (input.toMillis() > ONE_YEAR_FROM(station, startDateFilter).toMillis()) {
    return "End Date cannot be over 1 year after Start Date";
  }
  return "";
};

// ***** Employee Info Validation End ***** //

// **************************************** //

// ***** Base Schedule Validation Start ***** //

export const baseScheduleDailyValidation = (baseSchedule, dayString, contract) => {
  // naming?
  const currentDay = baseSchedule[dayString];

  if (isDayBlankOrOff(currentDay)) {
    return "";
  }

  const shiftLength = getShiftLength(currentDay);
  if (isShiftLessThanFourHours(shiftLength)) {
    return MINIMUM_SHIFT_LENGTH_WARNING__4_HOURS;
  }

  switch (contract) {
    case IAM_CONTRACT:
      if (isShiftLengthGreaterThanIAMMax(shiftLength)) {
        return MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
      }
      break;
    case TWU_CONTRACT:
      if (isShiftLengthGreaterThanTWUMax(shiftLength)) {
        return MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
      }
      break;
    case NON_CONTRACT:
      if (isShiftLengthGreaterThanNonContractMax(shiftLength)) {
        return MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
      }
      break;
    default:
      return "Uh oh, unknown contract";
  }

  if (doesShiftOverLapWithPreviousShift(baseSchedule, dayString, currentDay)) {
    return SHIFT_OVERLAPS_WITH_PREVIOUS_SHIFT_WARNING_MESSAGE;
  }

  return "";
};

function doesShiftOverLapWithPreviousShift(baseSchedule, dayString, currentDay) {
  const previousDay = getPreviousDay(baseSchedule, dayString);
  if (!isDayIncludedInAssignment(previousDay) || isDayBlankOrOff(previousDay)) {
    return false;
  }

  if (parseInt(previousDay.start.hr) > parseInt(previousDay.end.hr)) {
    if (parseInt(currentDay.start.hr) > parseInt(previousDay.end.hr)) {
      return false;
    } else if (parseInt(currentDay.start.hr) < parseInt(previousDay.end.hr)) {
      return true;
    } else {
      if (parseInt(currentDay.start.min) >= parseInt(previousDay.end.min)) {
        return false;
      } else {
        return true;
      }
    }
  } else {
    return false;
  }
}

function getPreviousDay(baseSchedule, day) {
  const currentDayIdx = WEEKDAYS_SHORT.indexOf(day);
  const previousDayIdx = getPreviousDayIdx(currentDayIdx);
  const previousDayString = WEEKDAYS_SHORT[previousDayIdx];
  return baseSchedule[previousDayString];
}

function getPreviousDayIdx(currentDayIdx) {
  let previousDayIdx = currentDayIdx - 1;
  if (previousDayIdx < 0) {
    previousDayIdx = 6;
  }
  return previousDayIdx;
}

function isShiftLessThanFourHours(shiftLength) {
  return shiftLength < FOUR_HOURS_IN_MINUTES;
}

function isShiftLengthGreaterThanIAMMax(shiftLength) {
  return shiftLength > MAX_IAM_SHIFT_LENGTH_MINUTES;
}

function isShiftLengthGreaterThanTWUMax(shiftLength) {
  return shiftLength > MAX_TWU_SHIFT_LENGTH_MINUTES;
}

function isShiftLengthGreaterThanNonContractMax(shiftLength) {
  return shiftLength > MAX_NON_CONTRACT_SHIFT_LENGTH_MINUTES;
}

function getShiftLength(day) {
  const startTime = getShiftTime(day.start);
  let endTime = getShiftTime(day.end);
  if (startTime > endTime) {
    endTime += TWENTY_FOUR_HOURS_IN_MINUTES;
  }
  return endTime - startTime;
}

function getShiftTime(shiftTime) {
  return getHoursInMins(shiftTime.hr) + getMins(shiftTime.min);
}

function getHoursInMins(hour) {
  return parseInt(hour) * 60;
}

function getMins(mins) {
  return parseInt(mins);
}

export const validateNoPartialSchedules = (baseSchedule) => {
  let partialScheduleError = "Cannot enter a partial schedule";
  if (isBaseScheduleBlank(baseSchedule)) {
    return "";
  } else if (areAnyDaysLeftBlank(baseSchedule)) {
    return partialScheduleError;
  } else if (areAllDaysOff(baseSchedule)) {
    return "Cannot enter all shifts as off";
  }
  return "";
};

export function areAnyDaysLeftBlank(baseSchedule) {
  let areAnyDaysLeftBlank = false;
  for (const day in baseSchedule) {
    const currentDay = baseSchedule[day];
    if (isDayIncludedInAssignment(currentDay)) {
      if (isDayBlank(currentDay) || isDayPartiallyFilledOut(currentDay)) {
        areAnyDaysLeftBlank = true;
        break;
      }
    }
  }
  return areAnyDaysLeftBlank;
}

export function isBaseScheduleBlank(baseSchedule) {
  let isBaseScheduleBlank = true;
  for (const day in baseSchedule) {
    const currentDay = baseSchedule[day];
    if (isDayIncludedInAssignment(currentDay)) {
      if (!isDayBlank(currentDay)) {
        isBaseScheduleBlank = false;
        break;
      }
    }
  }
  return isBaseScheduleBlank;
}

export function areAllDaysOff(baseSchedule) {
  let areAllDaysOff = true;
  for (const day in baseSchedule) {
    const currentDay = baseSchedule[day];
    if (isDayIncludedInAssignment(currentDay)) {
      if (!isDayOff(currentDay)) {
        areAllDaysOff = false;
        break;
      }
    }
  }
  return areAllDaysOff;
}

export function isDayIncludedInAssignment(day) {
  return !day.isNA;
}

function isDayPartiallyFilledOut(day) {
  const startHourFlag = isTimeFieldBlank(day.start.hr);
  const startMinFlag = isTimeFieldBlank(day.start.min);
  const endHourFlag = isTimeFieldBlank(day.end.hr);
  const endMinFlag = isTimeFieldBlank(day.end.min);
  return startHourFlag || startMinFlag || endHourFlag || endMinFlag;
}

function isDayOff(day) {
  const startHourFlag = isTimeFieldOff(day.start.hr);
  const startMinFlag = isTimeFieldOff(day.start.min);
  const endHourFlag = isTimeFieldOff(day.end.hr);
  const endMinFlag = isTimeFieldOff(day.end.min);
  return startHourFlag && startMinFlag && endHourFlag && endMinFlag;
}

function isDayBlankOrOff(day) {
  const startHourFlag = isTimeFieldBlankOrOff(day.start.hr);
  const startMinFlag = isTimeFieldBlankOrOff(day.start.min);
  const endHourFlag = isTimeFieldBlankOrOff(day.end.hr);
  const endMinFlag = isTimeFieldBlankOrOff(day.end.min);
  return startHourFlag && startMinFlag && endHourFlag && endMinFlag;
}

function isDayBlank(day) {
  const startHourFlag = isTimeFieldBlank(day.start.hr);
  const startMinFlag = isTimeFieldBlank(day.start.min);
  const endHourFlag = isTimeFieldBlank(day.end.hr);
  const endMinFlag = isTimeFieldBlank(day.end.min);
  return startHourFlag && startMinFlag && endHourFlag && endMinFlag;
}

function isTimeFieldBlankOrOff(timeField) {
  return isTimeFieldBlank(timeField) || isTimeFieldOff(timeField);
}

function isTimeFieldBlank(timeField) {
  return timeField === "";
}

function isTimeFieldOff(timeField) {
  return timeField === "OFF";
}
